import posthog, { type CaptureResult } from "posthog-js";
import type { identifyPostHogUser } from "./identify";

/**
 * PostHog product analytics configuration.
 *
 * Initializes whenever both REACT_APP_POSTHOG_PROJECT_TOKEN and
 * REACT_APP_POSTHOG_HOST are present. The deploy pipeline injects the dev
 * project token in develop and the prod project token in production; for
 * local testing, fill these in from 1Password (see .env.sample).
 *
 * Client-side storage: PostHog persists its own internal identifiers
 * (distinct_id = user.uuid, device_id, session_id, super_properties) to
 * localStorage by default. None of these are customer PII, third-party API
 * payloads, or Merge auth tokens.
 *
 * Autocapture, heatmaps, and session replay are all enabled site-wide.
 * Customer/user PII is masked at PII-bearing surfaces via `ph-no-capture`
 * (see .claude/rules/posthog.md for the surface inventory) and at portal-
 * mounted Tooltip / Listbox / Toast bodies via session_recording.maskTextSelector
 * below. Autocapture on Typeahead options that render PII is suppressed in
 * before_send when window.location.pathname starts with a PII_LISTBOX_PATHS
 * prefix. Pageview/pageleave fire on every route via the History API plugin
 * (defaults: '2026-01-30').
 */
export const posthogToken = process.env.REACT_APP_POSTHOG_PROJECT_TOKEN;
export const posthogHost = process.env.REACT_APP_POSTHOG_HOST;

// Routes whose Typeahead/listbox options render user/customer PII. The before_send
// branch below drops $autocapture events with attr__role="option" on these paths
// so the dropdown options don't leak in autocapture (their visible text is also
// masked in session replay via maskTextSelector). Add a path when introducing a
// new Typeahead that renders end-user-chosen content (LA names, org names, emails).
export const PII_LISTBOX_PATHS: string[] = ["/api-tester"];

// Collapse dynamic URL segments so dashboards don't fragment across UUIDs/slugs.
export const PATH_NORMALIZATION_RULES: Array<{ pattern: RegExp; replacement: string }> = [
  // Linked Accounts: /linked-accounts/account/<id> and /accounts/<id>
  {
    pattern: /\/linked-accounts\/account\/[^/?#]+/g,
    replacement: "/linked-accounts/account/:linkedAccountId",
  },
  { pattern: /\/accounts\/[^/?#]+/g, replacement: "/accounts/:linkedAccountId" },
  // Issues
  { pattern: /\/issues\/[^/?#]+/g, replacement: "/issues/:integrationIssueId" },
  // Integrations & Common Models by category
  { pattern: /\/integrations\/[^/?#]+/g, replacement: "/integrations/:category" },
  { pattern: /\/common-models\/[^/?#]+/g, replacement: "/common-models/:category" },
  // Configuration sub-resources
  {
    pattern: /\/configuration\/webhooks\/emitters\/[^/?#]+/g,
    replacement: "/configuration/webhooks/emitters/:webhookId",
  },
  // Field-mappings target-fields family. Ordered most-specific first so create/settings
  // sub-routes are matched before the bare-detail fallback. The negative lookahead on the
  // last two rules excludes "common-model-overrides" as a segment so its dedicated rules
  // (above) own that shape and the literal isn't relabeled as a target-field ID.
  {
    pattern: /\/configuration\/field-mappings\/target-fields\/[^/?#]+\/[^/?#]+\/create/g,
    replacement:
      "/configuration/field-mappings/target-fields/:fieldMappingTargetId/:integrationId/create",
  },
  {
    pattern:
      /\/configuration\/field-mappings\/target-fields\/common-model-overrides\/[^/?#]+\/settings/g,
    replacement:
      "/configuration/field-mappings/target-fields/common-model-overrides/:commonModelOverrideId/settings",
  },
  {
    pattern:
      /\/configuration\/field-mappings\/target-fields\/(?!common-model-overrides\/)[^/?#]+\/settings/g,
    replacement: "/configuration/field-mappings/target-fields/:fieldMappingTargetId/settings",
  },
  {
    pattern: /\/configuration\/field-mappings\/target-fields\/(?!common-model-overrides\/)[^/?#]+/g,
    replacement: "/configuration/field-mappings/target-fields/:fieldMappingTargetId",
  },
  {
    pattern: /\/configuration\/field-mappings\/common-model-overrides\/[^/?#]+/g,
    replacement: "/configuration/field-mappings/common-model-overrides/:commonModelOverrideId",
  },
  // API tester linked-account-type variant
  {
    pattern: /\/api-tester\/you-to-merge\/[^/?#]+/g,
    replacement: "/api-tester/you-to-merge/:linkedAccountType",
  },
];

export function normalizeUrl(raw: unknown): unknown {
  if (typeof raw !== "string" || !raw) return raw;
  try {
    const u = new URL(raw);
    u.search = "";
    let path = u.pathname;
    for (const { pattern, replacement } of PATH_NORMALIZATION_RULES) {
      path = path.replace(pattern, replacement);
    }
    u.pathname = path;
    return u.toString();
  } catch {
    return raw;
  }
}

// URL-shaped properties are full URLs; parse and normalize the pathname.
const URL_SHAPED_KEYS = [
  "$current_url",
  "$referrer",
  "$initial_current_url",
  "$initial_referrer",
  "$session_entry_url",
  "$session_entry_referrer",
] as const;

// Path-shaped properties are relative paths; run the regex rules directly.
const PATH_SHAPED_KEYS = [
  "$pathname",
  "$initial_pathname",
  "$prev_pageview_pathname",
  "$session_entry_pathname",
] as const;

function normalizeKnownUrlAndPathKeys(props: Record<string, unknown>): void {
  for (const key of URL_SHAPED_KEYS) {
    if (key in props) {
      props[key] = normalizeUrl(props[key]);
    }
  }
  for (const key of PATH_SHAPED_KEYS) {
    const value = props[key];
    if (typeof value === "string") {
      let p = value;
      for (const { pattern, replacement } of PATH_NORMALIZATION_RULES) {
        p = p.replace(pattern, replacement);
      }
      props[key] = p;
    }
  }
}

export const SECRET_PATTERNS = [
  /sk-[A-Za-z0-9_-]{16,}/,
  /sk_(test|live)_[A-Za-z0-9]{16,}/,
  /ghp_[A-Za-z0-9]{30,}/,
  /Bearer\s+[A-Za-z0-9._-]{20,}/i,
  /eyJ[A-Za-z0-9._-]+/,
  /AIzaSy[A-Za-z0-9_-]{30,}/, // Google API keys
  /gsk_[A-Za-z0-9]{40,}/, // Groq API keys
  /mg_[A-Za-z0-9]{30,}/, // Merge internal account tokens
  /xai-[A-Za-z0-9]{30,}/, // xAI API keys
];

export function looksSensitive(v: unknown): boolean {
  return typeof v === "string" && SECRET_PATTERNS.some((rx) => rx.test(v));
}

function scrubSensitiveStringValues(bag: Record<string, unknown>): void {
  for (const k of Object.keys(bag)) {
    if (looksSensitive(bag[k])) {
      bag[k] = "[redacted]";
    }
  }
}

export const posthogOptions = {
  api_host: posthogHost,
  ui_host: "https://us.posthog.com",
  autocapture: true,
  // Mask portal-mounted bodies that escape ancestor ph-no-capture: Typeahead/select
  // listboxes (LA names etc.), MUI Tooltip bodies (log URLs, org names),
  // react-toastify toast bodies (user/customer-supplied strings interpolated into toasts),
  // and the Inkeep Ask AI conversation history (typed user input + AI output, rendered in
  // an Inkeep-managed portal modal whose DOM React cannot reach via ancestor classes).
  session_recording: {
    maskTextSelector:
      "[role='listbox'], .MuiTooltip-tooltip, .Toastify__toast-body, .ikp-ai-chat-message-content",
  },
  enable_heatmaps: true,
  person_profiles: "identified_only" as const,
  opt_out_capturing_by_default: false,
  defaults: "2026-01-30" as const,
  debug: process.env.NODE_ENV !== "production",
  loaded: (ph: { register: (props: Record<string, unknown>) => void }) => {
    ph.register({ product: "unified" });
  },
  before_send: (event: CaptureResult | null): CaptureResult | null => {
    if (!event) return event;

    // $set / $set_once location varies by event type. Some events nest them
    // inside properties (event.properties.$set_once.$initial_pathname);
    // $identify attaches them as top-level siblings (event.$set_once.$initial_pathname).
    // Walk both locations defensively.
    if (event.properties) {
      normalizeKnownUrlAndPathKeys(event.properties);
      for (const nestedKey of ["$set_once", "$set"] as const) {
        const nested = (event.properties as Record<string, unknown>)[nestedKey];
        if (nested && typeof nested === "object") {
          normalizeKnownUrlAndPathKeys(nested as Record<string, unknown>);
        }
      }
    }
    for (const nestedKey of ["$set_once", "$set"] as const) {
      const nested = (event as unknown as Record<string, unknown>)[nestedKey];
      if (nested && typeof nested === "object") {
        normalizeKnownUrlAndPathKeys(nested as Record<string, unknown>);
      }
    }

    // Drop autocapture on Typeahead/listbox options when the current page renders
    // user/customer PII as the option text. Listbox bodies are also masked in
    // session replay via maskTextSelector; this branch covers the autocapture event.
    if (
      event.event === "$autocapture" &&
      typeof event.properties?.$elements_chain === "string" &&
      event.properties.$elements_chain.includes('attr__role="option"')
    ) {
      const path = typeof window !== "undefined" ? window.location.pathname : "";
      if (PII_LISTBOX_PATHS.some((prefix) => path.startsWith(prefix))) return null;
    }

    if (
      event.event === "$autocapture" &&
      event.properties &&
      typeof event.properties.$elements_chain === "string"
    ) {
      const firstSegment = event.properties.$elements_chain.split(";")[0];
      if (firstSegment) {
        const tagMatch = firstSegment.match(/^([a-zA-Z][a-zA-Z0-9-]*)/);
        if (tagMatch) event.properties.element_tag = tagMatch[1];
        const idMatch = firstSegment.match(/(^|[^_])attr_id="([^"]*)"/);
        const nameMatch = firstSegment.match(/attr__name="([^"]*)"/);
        if (idMatch && idMatch[2]) {
          event.properties.field_name = idMatch[2];
        } else if (nameMatch && nameMatch[1]) {
          event.properties.field_name = nameMatch[1];
        }
      }
    }

    if (event.properties) {
      scrubSensitiveStringValues(event.properties);
      for (const nestedKey of ["$set_once", "$set"] as const) {
        const nested = (event.properties as Record<string, unknown>)[nestedKey];
        if (nested && typeof nested === "object") {
          scrubSensitiveStringValues(nested as Record<string, unknown>);
        }
      }
    }
    for (const nestedKey of ["$set_once", "$set"] as const) {
      const nested = (event as unknown as Record<string, unknown>)[nestedKey];
      if (nested && typeof nested === "object") {
        scrubSensitiveStringValues(nested as Record<string, unknown>);
      }
    }

    return event;
  },
};

/**
 * Dev-only debug hook so local debugging can call identify / inspect state
 * from the browser console. Gated by NODE_ENV; never exposed in production
 * builds. Matches Gateway/AH.
 */
export function exposeDevDebugHook(identifyFn: typeof identifyPostHogUser): void {
  if (process.env.NODE_ENV !== "production") {
    const w = window as unknown as {
      posthog: typeof posthog;
      identifyPostHogUser: typeof identifyPostHogUser;
    };
    w.posthog = posthog;
    w.identifyPostHogUser = identifyFn;
  }
}
